import React, { useState, useEffect } from 'react'
import {
  CheckCircle,
  XCircle,
  Clock,
  TrendingUp,
  Users,
  FileText,
  Download,
  Calendar,
  MapPin,
  AlertTriangle
} from 'lucide-react'
import type { NdaAcceptance } from '../lib/storage'

interface NdaAnalyticsDashboardProps {
  tenantId: string
}

interface NdaStats {
  totalAcceptances: number
  activeAcceptances: number
  expiredAcceptances: number
  revokedAcceptances: number
  acceptanceRate: number
  recentAcceptances: NdaAcceptance[]
  expiringExpiringSoon: NdaAcceptance[]
  topCountries: { country: string, count: number }[]
  acceptancesByDay: { date: string, count: number }[]
}

const NdaAnalyticsDashboard: React.FC<NdaAnalyticsDashboardProps> = ({ tenantId }) => {
  const [stats, setStats] = useState<NdaStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [timeRange, setTimeRange] = useState<'7d' | '30d' | '90d' | 'all'>('30d')

  useEffect(() => {
    let active = true

    const fetchAnalytics = async () => {
      if (active) {
        setLoading(true)
        setError(null)
      }

      try {
        const response = await fetch(`/api/nda/analytics?tenantId=${tenantId}&timeRange=${timeRange}`)
        if (!response.ok) throw new Error('Failed to fetch NDA analytics')

        const data = await response.json()

        if (active) {
          setStats(data)
        }
      } catch (err: any) {
        if (active) {
          setError(err.message || 'Failed to load analytics')
        }
      } finally {
        if (active) {
          setLoading(false)
        }
      }
    }

    fetchAnalytics()
    return () => { active = false }
  }, [tenantId, timeRange])

  const handleExport = async (format: 'csv' | 'pdf') => {
    try {
      const response = await fetch(`/api/nda/export?tenantId=${tenantId}&format=${format}&timeRange=${timeRange}`)
      if (!response.ok) throw new Error('Failed to export data')

      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `nda-report-${new Date().toISOString().split('T')[0]}.${format}`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (err: any) {
      console.error('[NdaAnalytics] Export failed:', err)
      alert('Failed to export: ' + (err instanceof Error ? err.message : 'Unknown error'))
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin h-8 w-8 border-4 border-primary-600 border-t-transparent rounded-full" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-600">{error}</p>
      </div>
    )
  }

  if (!stats) return null

  const statCards = [
    {
      label: 'Total Acceptances',
      value: stats.totalAcceptances,
      icon: Users,
      color: 'text-blue-600 bg-blue-100'
    },
    {
      label: 'Active NDAs',
      value: stats.activeAcceptances,
      icon: CheckCircle,
      color: 'text-green-600 bg-green-100'
    },
    {
      label: 'Expired',
      value: stats.expiredAcceptances,
      icon: Clock,
      color: 'text-yellow-600 bg-yellow-100'
    },
    {
      label: 'Acceptance Rate',
      value: `${stats.acceptanceRate}%`,
      icon: TrendingUp,
      color: 'text-purple-600 bg-purple-100'
    }
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">NDA Analytics</h3>
          <p className="text-sm text-gray-600 mt-1">Track acceptance rates and compliance status</p>
        </div>
        <div className="flex items-center gap-3">
          {/* Time Range Selector */}
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value as any)}
            className="px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
            <option value="all">All time</option>
          </select>

          {/* Export Buttons */}
          <button
            onClick={() => handleExport('csv')}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 text-sm font-medium"
          >
            <Download className="h-4 w-4" />
            Export CSV
          </button>
          <button
            onClick={() => handleExport('pdf')}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium"
          >
            <Download className="h-4 w-4" />
            Export PDF
          </button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((card, index) => {
          const Icon = card.icon
          return (
            <div key={index} className="bg-white border border-gray-200 rounded-lg p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{card.label}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{card.value}</p>
                </div>
                <div className={`p-3 rounded-lg ${card.color}`}>
                  <Icon className="h-6 w-6" />
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Expiring Soon Warning */}
      {stats.expiringExpiringSoon.length > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-medium text-yellow-900">NDAs Expiring Soon</p>
              <p className="text-sm text-yellow-700 mt-1">
                {stats.expiringExpiringSoon.length} NDA{stats.expiringExpiringSoon.length > 1 ? 's' : ''} will expire in the next 30 days
              </p>
              <div className="mt-3 space-y-2">
                {stats.expiringExpiringSoon.slice(0, 5).map((acceptance) => (
                  <div key={acceptance.id} className="flex items-center justify-between text-sm">
                    <span className="text-yellow-900">{acceptance.userEmail}</span>
                    <span className="text-yellow-700">
                      Expires {new Date(acceptance.expiresAt!).toLocaleDateString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Acceptances */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <FileText className="h-5 w-5 text-gray-600" />
            Recent Acceptances
          </h4>
          <div className="space-y-3">
            {stats.recentAcceptances.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">No recent acceptances</p>
            ) : (
              stats.recentAcceptances.map((acceptance) => (
                <div key={acceptance.id} className="flex items-start justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{acceptance.userEmail}</p>
                    {acceptance.userCompany && (
                      <p className="text-xs text-gray-600">{acceptance.userCompany}</p>
                    )}
                    <div className="flex items-center gap-2 mt-1">
                      <Calendar className="h-3 w-3 text-gray-400" />
                      <p className="text-xs text-gray-500">
                        {new Date(acceptance.acceptedAt).toLocaleDateString()}
                      </p>
                      {acceptance.geolocation?.country && (
                        <>
                          <MapPin className="h-3 w-3 text-gray-400 ml-2" />
                          <p className="text-xs text-gray-500">
                            {acceptance.geolocation.city}, {acceptance.geolocation.country}
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                  <span className={`px-2 py-1 text-xs font-medium rounded ${
                    acceptance.status === 'active'
                      ? 'bg-green-100 text-green-700'
                      : acceptance.status === 'expired'
                      ? 'bg-yellow-100 text-yellow-700'
                      : 'bg-red-100 text-red-700'
                  }`}>
                    {acceptance.status}
                  </span>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Geographic Distribution */}
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <MapPin className="h-5 w-5 text-gray-600" />
            Geographic Distribution
          </h4>
          <div className="space-y-3">
            {stats.topCountries.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-4">No geographic data available</p>
            ) : (
              stats.topCountries.map((item, index) => (
                <div key={index} className="flex items-center justify-between">
                  <span className="text-sm text-gray-700">{item.country || 'Unknown'}</span>
                  <div className="flex items-center gap-2">
                    <div className="w-32 bg-gray-200 rounded-full h-2">
                      <div
                        className="bg-blue-600 h-2 rounded-full"
                        style={{ width: `${(item.count / stats.totalAcceptances) * 100}%` }}
                      />
                    </div>
                    <span className="text-sm font-medium text-gray-900 w-8 text-right">{item.count}</span>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Acceptance Trend Chart (Simple Bar Chart) */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-gray-600" />
          Acceptance Trend
        </h4>
        <div className="h-64 flex items-end justify-between gap-2">
          {stats.acceptancesByDay.map((day, index) => {
            const maxCount = Math.max(...stats.acceptancesByDay.map(d => d.count), 1)
            const height = (day.count / maxCount) * 100
            return (
              <div key={index} className="flex-1 flex flex-col items-center">
                <div
                  className="w-full bg-blue-600 rounded-t hover:bg-blue-700 transition-colors cursor-pointer relative group"
                  style={{ height: `${height}%`, minHeight: day.count > 0 ? '4px' : '0' }}
                >
                  <div className="absolute bottom-full mb-2 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs py-1 px-2 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                    {day.count} acceptance{day.count !== 1 ? 's' : ''}
                  </div>
                </div>
                <p className="text-xs text-gray-500 mt-2 transform -rotate-45 origin-top-left">
                  {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </p>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default NdaAnalyticsDashboard
